import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { QuestionMarkCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { Dispatch, Fragment, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useResetCustomPricesMutation } from '../../lib/graphql.generated';

interface ResetPricesAlertProps {
  id: string;
  show: boolean;
  setClose: Dispatch<SetStateAction<boolean>>;
  next: () => void;
}
export default function ResetPricesAlert({
  show,
  setClose,
  id,
  next,
}: ResetPricesAlertProps) {
  const { t } = useTranslation();
  const close = () => setClose(false);
  const [setBasePrice, { data: basePriceResult }] = useResetCustomPricesMutation();

  const handleOnClick = () => {
    void setBasePrice({ variables: { id } });
  };

  useEffect(() => {
    if (basePriceResult?.resetConfigurationPrices.id == id) {
      close();
      void next();
    }
  });
  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        as="div"
        id="ResetPricesConfirm"
        open={show}
        onClose={close}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="min-h-screen px-4 z-10 text-center">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  onClick={close}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                >
                  <span className="sr-only">{t('modal.closeButton')}</span>
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="sm:flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
                  <QuestionMarkCircleIcon className="h-6 w-6 text-gray-600" />
                </div>
                <div className="mt-3 text-center items-center sm:mt-0 sm:ml-4 sm:text-left flex-col">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    {t('panel.modal.resetPricesConfirm')}
                  </Dialog.Title>
                </div>
              </div>
              <div className="mt-4 pl-4">
                <p className="text-sm text-gray-500">
                  {t('panel.modal.resetConfirmDetail')}
                </p>
              </div>
              <div className="mt-4 py-3 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 mx-2 text-sm font-medium text-white bg-red-400 border border-transparent rounded-full hover:bg-red-600 focus:outline-none"
                  onClick={handleOnClick}
                >
                  {t('panel.modal.resetConfirmButton')}
                </button>
                <button
                  type="button"
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-transparent rounded-full hover:bg-gray-200 focus:outline-none"
                  onClick={close}
                >
                  {t('button.cancel')}
                </button>
                <input type="hidden" className="sr-only" />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
